import React from 'react'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import {
  formatReleaseDate,
  getChangeTypeLabel,
  getLocalizedChanges,
} from './releaseNotes'
import type { ReleaseNote } from './releaseNotes'

interface ReleaseNoteCardProps {
  language: string
  note: ReleaseNote
}

const ReleaseNoteCard: React.FC<ReleaseNoteCardProps> = ({ language, note }) => {
  const theme = useTheme()
  const changeGroups = Object.entries(getLocalizedChanges(note.changes, language))
    .filter(([, items]) => items.length > 0)

  return (
    <Stack
      spacing={1}
      sx={{
        position: 'relative',
        p: 1.5,
        pl: 2,
        borderRadius: '8px',
        backgroundColor: theme.palette.grey[50],
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: '3px',
          backgroundColor: theme.palette.primary.main,
          borderRadius: 0
        }
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" flexWrap="wrap">
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" minWidth={0} flex={1}>
          {note.source && (
            <Typography
              sx={{
                px: 0.75,
                py: 0.25,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: '6px',
                backgroundColor: 'white',
                color: theme.palette.grey[700],
                fontSize: 10,
                fontWeight: 600,
                lineHeight: 1.4,
                flexShrink: 0
              }}
            >
              {note.source}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: '#282828',
              minWidth: 0,
              overflowWrap: 'anywhere'
            }}
          >
            {note.version}
          </Typography>
        </Stack>
        <Typography
          sx={{
            px: 0.75,
            py: 0.25,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: '6px',
            backgroundColor: 'white',
            color: theme.palette.grey[600],
            fontSize: 10,
            fontWeight: 500,
            lineHeight: 1.4,
            flexShrink: 0
          }}
        >
          {formatReleaseDate(note.date, language)}
        </Typography>
      </Stack>
      {changeGroups.map(([type, items]) => (
        <Stack key={`${note.version}-${type}`} spacing={0.75}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: theme.palette.primary.main }}>
            {getChangeTypeLabel(type, language)}
          </Typography>
          <Stack spacing={0.5}>
            {items.map(change => (
              <Stack
                key={change}
                direction="row"
                spacing={0.75}
                alignItems="baseline"
                sx={{
                  minWidth: 0
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    flexShrink: 0,
                    transform: 'translateY(-1px)'
                  }}
                />
                <Typography
                  sx={{
                    minWidth: 0,
                    fontSize: 12,
                    lineHeight: 1.55,
                    fontWeight: 400,
                    color: theme.palette.grey[800],
                    overflowWrap: 'anywhere'
                  }}
                >
                  {change}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  )
}

export default ReleaseNoteCard
